/**
 * Thin wrapper around amazon-cognito-identity-js.
 *
 * Why a wrapper at all: the SDK's callback-based API isn't ergonomic in
 * 2026-React (it predates promises in the public surface). This module
 * exposes the half-dozen flows the dashboard actually needs as plain
 * async functions that resolve with a normalised shape, so Login.jsx
 * and the password-management pages don't each reimplement the same
 * callback-to-promise glue.
 *
 * Token storage: we let the SDK do its own localStorage caching (under
 * `CognitoIdentityServiceProvider.{poolClientId}.LastAuthUser` and
 * related keys). We layer on a single `auth_token` key holding the
 * current ID token so the existing api.js Bearer header logic keeps
 * working — getIdToken() refreshes it transparently if the access
 * token is stale but the refresh token is still valid.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const COGNITO_USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const COGNITO_APP_CLIENT_ID = process.env.REACT_APP_COGNITO_APP_CLIENT_ID;
const COGNITO_REGION = process.env.REACT_APP_COGNITO_REGION || 'eu-central-1';

if (!COGNITO_USER_POOL_ID || !COGNITO_APP_CLIENT_ID) {
  // Loud during local dev so a missing env doesn't manifest as a
  // confusing "User pool not found" error 3 clicks deep into Login.
  // eslint-disable-next-line no-console
  console.error(
    'Cognito env vars missing, REACT_APP_COGNITO_USER_POOL_ID and ' +
    'REACT_APP_COGNITO_APP_CLIENT_ID must be set at build time.'
  );
}

const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_USER_POOL_ID,
  ClientId: COGNITO_APP_CLIENT_ID,
});

// `export const cognitoConfig` was here — region/userPoolId/clientId re-exported as one object.
// A full import/export graph over frontend/src found zero importers, and it was the only unused
// export in the entire frontend. The pool itself is constructed above from the same three
// values, which is what every caller actually uses. Deleted 2026-08-02.

// ── Login flow ───────────────────────────────────────────────────────────────
//
// authenticateUser is a multi-step state machine on Cognito's side. We
// return an object describing the NEXT step so the caller (Login.jsx)
// can render the right UI:
//   { status: 'success', tokens }
//   { status: 'newPasswordRequired', cognitoUser, requiredAttributes }
//   { status: 'mfaSetup', cognitoUser, secretCode, qrUrl }
//   { status: 'totpRequired', cognitoUser }
//   { status: 'error', message }

export function signIn(username, password) {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    cognitoUser.setAuthenticationFlowType('USER_SRP_AUTH');

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        persistTokens(session, username);
        resolve({ status: 'success', tokens: extractTokens(session, username) });
      },
      onFailure: (err) => {
        resolve({ status: 'error', message: err.message || 'Invalid credentials' });
      },
      newPasswordRequired: (userAttributes /* , requiredAttributes */) => {
        // Remove read-only attrs Cognito won't let us re-submit.
        delete userAttributes.email_verified;
        delete userAttributes.email;
        resolve({
          status: 'newPasswordRequired',
          cognitoUser,
          userAttributes,
        });
      },
      mfaSetup: () => {
        // Cognito requires us to associate a software token before it
        // can issue the QR code. associateSoftwareToken's callback gives
        // us the secretCode we wrap into a TOTP URI.
        cognitoUser.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            const qrUrl = buildOtpAuthUrl(username, secretCode);
            resolve({ status: 'mfaSetup', cognitoUser, secretCode, qrUrl });
          },
          onFailure: (err) => {
            resolve({ status: 'error', message: err.message || 'MFA setup failed' });
          },
        });
      },
      totpRequired: () => {
        resolve({ status: 'totpRequired', cognitoUser });
      },
    });
  });
}

// Step 2 of the first-login flow: user types a new password.
export function completeNewPasswordChallenge(cognitoUser, newPassword, userAttributes) {
  return new Promise((resolve) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => {
        const username = cognitoUser.getUsername();
        persistTokens(session, username);
        resolve({ status: 'success', tokens: extractTokens(session, username) });
      },
      onFailure: (err) => {
        resolve({ status: 'error', message: err.message || 'Failed to set new password' });
      },
      mfaSetup: () => {
        // After the password change Cognito requires us to enrol TOTP.
        const username = cognitoUser.getUsername();
        cognitoUser.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            const qrUrl = buildOtpAuthUrl(username, secretCode);
            resolve({ status: 'mfaSetup', cognitoUser, secretCode, qrUrl });
          },
          onFailure: (err) => {
            resolve({ status: 'error', message: err.message || 'MFA setup failed' });
          },
        });
      },
    });
  });
}

// Step 3 of the first-login flow (only if `mfaSetup` came up): user
// scans the QR code and types the 6-digit TOTP from their authenticator.
export function verifyMfaSetup(cognitoUser, totpCode) {
  return new Promise((resolve) => {
    cognitoUser.verifySoftwareToken(totpCode, 'authenticator', {
      onSuccess: (session) => {
        cognitoUser.setUserMfaPreference(
          null,
          { PreferredMfa: true, Enabled: true },
          (err) => {
            if (err) {
              resolve({ status: 'error', message: err.message || 'Failed to enable MFA' });
              return;
            }
            const username = cognitoUser.getUsername();
            persistTokens(session, username);
            resolve({ status: 'success', tokens: extractTokens(session, username) });
          }
        );
      },
      onFailure: (err) => {
        resolve({ status: 'error', message: err.message || 'Invalid TOTP code' });
      },
    });
  });
}

// Returning-user flow: Cognito has already enrolled TOTP for this user,
// so authenticateUser → totpRequired → user types the 6-digit code → done.
export function submitTotpChallenge(cognitoUser, totpCode) {
  return new Promise((resolve) => {
    cognitoUser.sendMFACode(
      totpCode,
      {
        onSuccess: (session) => {
          const username = cognitoUser.getUsername();
          persistTokens(session, username);
          resolve({ status: 'success', tokens: extractTokens(session, username) });
        },
        onFailure: (err) => {
          resolve({ status: 'error', message: err.message || 'Invalid TOTP code' });
        },
      },
      'SOFTWARE_TOKEN_MFA'
    );
  });
}

// ── Forgot password ─────────────────────────────────────────────────────────

export function forgotPasswordStart(username) {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve({ status: 'codeSent', cognitoUser }),
      inputVerificationCode: () => resolve({ status: 'codeSent', cognitoUser }),
      onFailure: (err) =>
        resolve({ status: 'error', message: err.message || 'Failed to start reset' }),
    });
  });
}

export function forgotPasswordConfirm(cognitoUser, code, newPassword) {
  return new Promise((resolve) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve({ status: 'success' }),
      onFailure: (err) =>
        resolve({ status: 'error', message: err.message || 'Failed to reset password' }),
    });
  });
}

// ── Self-service password change (already logged in) ────────────────────────

export function changePassword(oldPassword, newPassword) {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve({ status: 'error', message: 'Not signed in' });
      return;
    }
    cognitoUser.getSession((err) => {
      if (err) {
        resolve({ status: 'error', message: err.message || 'Session expired' });
        return;
      }
      cognitoUser.changePassword(oldPassword, newPassword, (cpErr) => {
        if (cpErr) {
          resolve({ status: 'error', message: cpErr.message || 'Failed to change password' });
          return;
        }
        resolve({ status: 'success' });
      });
    });
  });
}

// ── Token plumbing ──────────────────────────────────────────────────────────

// Promise-dedupe: when several widgets concurrently call apiFetch (the
// Dashboard fires 5+ requests in parallel on first load), they used to
// race each other through getSession()/refreshSession(). Cognito
// occasionally rejects the loser of that race and the SDK returns an
// error → api.js used to interpret that as "session dead" and sign the
// user out. Hold a single Promise here so concurrent calls await the
// same refresh.
let _inFlightTokenPromise = null;

function _doGetIdToken(forceRefresh = false) {
  return new Promise((resolve) => {
   try {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    // forceRefresh: skip the cached-session check, go straight to a
    // refresh-token round-trip. Used by api.js when a fresh 401 comes
    // back — the cached session looked valid but the backend rejected
    // it (clock drift, JWKS rotation, etc.), so we explicitly burn the
    // refresh token for a new ID.
    if (forceRefresh) {
      cognitoUser.getSession((errPre, sessionPre) => {
        if (errPre || !sessionPre) { signOut(); resolve(null); return; }
        const refreshToken = sessionPre.getRefreshToken();
        if (!refreshToken || !refreshToken.getToken()) {
          signOut();
          resolve(null);
          return;
        }
        cognitoUser.refreshSession(refreshToken, (err, session) => {
          if (err || !session || !session.isValid()) { signOut(); resolve(null); return; }
          const idToken = session.getIdToken().getJwtToken();
          try { localStorage.setItem('auth_token', idToken); } catch (_) { /* ignore */ }
          resolve(idToken);
        });
      });
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        // Refresh token has expired (NotAuthorizedException) or the session
        // is otherwise unrecoverable. Clear stale localStorage so App.jsx
        // drops back to the login screen immediately rather than staying
        // in the authenticated shell with no data.
        signOut();
        resolve(null);
        return;
      }
      const idToken = session.getIdToken().getJwtToken();
      try { localStorage.setItem('auth_token', idToken); } catch (_) { /* ignore */ }
      resolve(idToken);
    });
   } catch (e) {
      // Defensive: getCurrentUser()/getSession can throw synchronously if the
      // SDK's localStorage cache is partial or corrupt (e.g. after 30 days of
      // refresh churn or a desync between the SDK keys and our auth_token key).
      // A synchronous throw here would REJECT this promise, so the caller's
      // `await getCurrentIdToken()` would throw instead of getting null — and
      // api.js would never reach its "no token → log out" branch, stranding the
      // user in the authenticated shell with a dead token (blank dashboard, no
      // login). Swallow it and resolve null so the session-expired path always
      // runs. See getCurrentIdToken() for the promise-dedupe wrapper.
      // eslint-disable-next-line no-console
      console.warn('getIdToken failed, treating as signed-out:', e && e.message);
      resolve(null);
   }
  });
}

// Called by api.js before every request. Returns the current ID token
// or null. If the access token is expired but the refresh token is
// valid, getSession() transparently refreshes (no UI prompt).
export function getCurrentIdToken() {
  if (_inFlightTokenPromise) return _inFlightTokenPromise;
  _inFlightTokenPromise = _doGetIdToken(false).finally(() => {
    _inFlightTokenPromise = null;
  });
  return _inFlightTokenPromise;
}

// Used by api.js's 401 retry path — bypasses the cache and forces a
// fresh refresh-token exchange.
export function forceRefreshIdToken() {
  return _doGetIdToken(true);
}

export function signOut() {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
  localStorage.removeItem('auth_token');
  localStorage.removeItem('admin_role');
  localStorage.removeItem('admin_username');
  localStorage.removeItem('admin_email');
}

// Called when the session is no longer recoverable (refresh token expired,
// a persistent 401, or a corrupt token cache). Clears all auth state and
// notifies the React tree via a window event so App.jsx can swap to the
// Login view IN PLACE — no full-page `window.location.href` redirect.
//
// Why an event instead of a hard redirect: App.jsx reads its auth state
// once at mount and is otherwise non-reactive, so before this the only way
// back to Login on mid-session expiry was api.js forcing a full reload. If
// that reload didn't fire cleanly (a thrown token promise, a hung refresh),
// the user was stranded in the authenticated shell with a dead token — a
// blank dashboard and no path to login. Dispatching a DOM event keeps the
// recovery inside React and independent of any single redirect firing.
export function handleSessionExpired() {
  signOut();
  try {
    window.dispatchEvent(new CustomEvent('ross:auth-expired'));
  } catch (_) {
    // CustomEvent unavailable (non-browser/test env) — storage is already
    // cleared, so a subsequent mount will land on Login regardless.
  }
}

// ── Internals ───────────────────────────────────────────────────────────────

function extractTokens(session, username) {
  const idToken = session.getIdToken();
  const claims = idToken.payload || {};
  // Role mirror of api/jwt_validator._role_from_groups so the dashboard
  // can render the right sidebar items immediately, without an extra
  // backend round-trip.
  const groups = claims['cognito:groups'] || [];
  const role = pickHighestRole(groups);
  return {
    idToken: idToken.getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    username,
    email: claims.email || null,
    role,
    groups,
  };
}

function persistTokens(session, username) {
  const tokens = extractTokens(session, username);
  localStorage.setItem('auth_token', tokens.idToken);
  localStorage.setItem('admin_role', tokens.role);
  localStorage.setItem('admin_username', tokens.username);
  if (tokens.email) localStorage.setItem('admin_email', tokens.email);
}

function pickHighestRole(groups) {
  if (groups.includes('superadmin')) return 'superadmin';
  if (groups.includes('admin')) return 'admin';
  return 'analyst';
}

function buildOtpAuthUrl(username, secretCode) {
  // Standard otpauth:// URI consumed by Google Authenticator / 1Password /
  // Authy etc. Issuer becomes the TOTP entry's display name. We use
  // "Ross Multitenant Admin (Dev)" / "(Stage)" / "(Prod)" so the user can
  // tell at a glance which env's code they're looking at. Fall back to
  // the pool id if the env wasn't baked into the build (e.g. local dev
  // with no Cognito wiring).
  const envSuffix = process.env.REACT_APP_COGNITO_ENV_DISPLAY
    || COGNITO_USER_POOL_ID
    || 'unknown';
  const issuer = `Ross Multitenant Admin (${envSuffix})`;
  return (
    `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(username)}` +
    `?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`
  );
}
